import { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
    collection,
    query,
    where,
    onSnapshot,
    orderBy,
    Timestamp,
    doc,
    DocumentData
} from 'firebase/firestore';

interface UseFirebaseOrdersResult<T> {
    data: T | null;
    loading: boolean;
    error: Error | null;
}

/**
 * Custom hook to fetch delivery orders in real-time from Firebase Firestore
 * @param restaurantId - The restaurant ID to filter orders
 * @param startDate - Start date for filtering (deliverAt >= startDate)
 * @param endDate - End date for filtering (deliverAt <= endDate)
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseDeliveryOrders = (
    restaurantId: string,
    startDate?: Date,
    endDate?: Date
): UseFirebaseOrdersResult<{ orders: any[] }> => {
    const [data, setData] = useState<{ orders: any[] } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Convert dates to timestamps for dependency comparison
    const startTimestamp = startDate ? startDate.getTime() : null;
    const endTimestamp = endDate ? endDate.getTime() : null;

    useEffect(() => {
        if (!restaurantId) {
            setLoading(false);
            return;
        }
        setLoading(true);

        // Build the query
        const ordersRef = collection(db, 'Orders');
        let q = query(
            ordersRef,
            where('restaurantId', '==', restaurantId),
            where('isDeliveryOrder', '==', true),
            where('orderStatus', '!=', 'InAppCart')
        );
        // Add date range filters if provided
        if (startDate) {
            const startTS = Timestamp.fromDate(startDate);
            q = query(q, where('deliverAt', '>=', startTS));
        }

        if (endDate) {
            const endTS = Timestamp.fromDate(endDate);
            q = query(q, where('deliverAt', '<=', endTS));
        }

        // Add ordering
        q = query(q, orderBy('createdAt', 'desc'));

        // Set up real-time listener
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const orders = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as any));
                setData({ orders });
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error('❌ Error fetching delivery orders:', err);
                console.error('  Error code:', err.code);
                console.error('  Error message:', err.message);
                setError(err as Error);
                setLoading(false);
            }
        );

        // Cleanup listener on unmount
        return () => {
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restaurantId, startTimestamp, endTimestamp]);
    return { data, loading, error };
};

/**
 * Custom hook to fetch all orders (not just delivery) in real-time from Firebase Firestore
 * @param restaurantId - The restaurant ID to filter orders
 * @param startDate - Start date for filtering (deliverAt >= startDate)
 * @param endDate - End date for filtering (deliverAt <= endDate)
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseOrders = (
    restaurantId: string,
    startDate?: Date,
    endDate?: Date
): UseFirebaseOrdersResult<{ orders: any[] }> => {
    const [data, setData] = useState<{ orders: any[] } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Convert dates to timestamps for dependency comparison
    const startTimestamp = startDate ? startDate.getTime() : null;
    const endTimestamp = endDate ? endDate.getTime() : null;

    useEffect(() => {
        if (!restaurantId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Build the query - fetch ALL orders for this restaurant
        const ordersRef = collection(db, 'Orders');
        let q = query(
            ordersRef,
            where('restaurantId', '==', restaurantId),
            where('isDeliveryOrder', '==', false),
            where('orderStatus', '!=', 'InAppCart')
        );

        // Add date range filters if provided
        if (startDate) {
            const startTS = Timestamp.fromDate(startDate);
            q = query(q, where('deliverAt', '>=', startTS));
        }

        if (endDate) {
            const endTS = Timestamp.fromDate(endDate);
            q = query(q, where('deliverAt', '<=', endTS));
        }

        // Add ordering
        q = query(q, orderBy('createdAt', 'desc'));

        // Set up real-time listener
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const orders = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as any));

                setData({ orders });
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error('❌ Error fetching all orders:', err);
                console.error('  Error code:', err.code);
                console.error('  Error message:', err.message);
                setError(err as Error);
                setLoading(false);
            }
        );

        // Cleanup listener on unmount
        return () => {
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restaurantId, startTimestamp, endTimestamp]);
    return { data, loading, error };
};

/**
 * Custom hook to fetch cart summaries in real-time from Firebase Firestore
 * Path: /Restaurants/{restaurantId}/inventories/{dateString}/cartSummaries/{dateString}
 * @param restaurantId - The restaurant ID
 * @param date - The date to fetch cart summaries for (defaults to current date)
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseCartSummaries = (restaurantId: string, date?: Date): UseFirebaseOrdersResult<DocumentData> => {
    const [data, setData] = useState<DocumentData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!restaurantId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Format date as YYYYMMDD (e.g., 20260105)
        const targetDate = date || new Date();
        const year = targetDate.getFullYear();
        const month = String(targetDate.getMonth() + 1).padStart(2, '0');
        const day = String(targetDate.getDate()).padStart(2, '0');
        const dateString = `${year}${month}${day}`;

        // Query for cart summaries: /Restaurants/{restaurantId}/inventories/{dateString}/projections/cartSummaries/{dateString}
        const cartSummariesRef = doc(
            db,
            'Restaurants',
            restaurantId,
            'inventories',
            dateString,
            'projections',
            'cartSummaries'
        );

        // Set up real-time listener
        const unsubscribe = onSnapshot(
            cartSummariesRef,
            (snapshot) => {
                if (snapshot.exists()) {
                    setData({
                        id: snapshot.id,
                        dateString,
                        ...snapshot.data()
                    });
                } else {
                    setData(null);
                }
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error('Error fetching cart summaries:', err);
                setError(err as Error);
                setLoading(false);
            }
        );

        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restaurantId, date?.getTime()]);

    return { data, loading, error };
};

export const useRestaurantDetails = (restaurantId: string): UseFirebaseOrdersResult<DocumentData> => {
    const [data, setData] = useState<DocumentData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!restaurantId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Query for cart summaries: /Restaurants/{restaurantId}
        const cartSummariesRef = doc(
            db,
            'Restaurants',
            restaurantId
        );

        // Set up real-time listener
        const unsubscribe = onSnapshot(
            cartSummariesRef,
            (snapshot) => {
                if (snapshot.exists()) {
                    setData({
                        id: snapshot.id,
                        ...snapshot.data()
                    });
                } else {
                    setData(null);
                }
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error('Error fetching cart summaries:', err);
                setError(err as Error);
                setLoading(false);
            }
        );

        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restaurantId]);
    return { data, loading, error };
};

export const useCartToCartOrders = (
    restaurantId: string,
    startDate?: Date | Timestamp | string,
): UseFirebaseOrdersResult<{ orders: any[] }> => {
    const [data, setData] = useState<{ orders: any[] } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Convert dates to timestamps for dependency comparison
    const startTimestamp = startDate instanceof Date ? startDate.getTime() : 
                          startDate instanceof Timestamp ? startDate.toMillis() : 
                          typeof startDate === 'string' ? new Date(startDate).getTime() : null;

    useEffect(() => {
        if (!restaurantId) {
                setLoading(false);
                return;
        }

        setLoading(true);

        // Build the query - fetch ALL orders for this restaurant
        const ordersRef = collection(db, 'CartItemRequests');
        let q = query(
            ordersRef,
            where('restaurantId', '==', restaurantId),
        );

        // Add date range filters if provided
        if (startDate) {
            let dateObj: Date;
            if (startDate instanceof Date) {
                dateObj = startDate;
            } else if (startDate instanceof Timestamp) {
                dateObj = startDate.toDate();
            } else {
                // Handle string date
                dateObj = new Date(startDate);
            }
            
            // Set to start of day (00:00:00)
            const startOfDay = new Date(dateObj);
            startOfDay.setHours(0, 0, 0, 0);
            
            // Set to end of day (23:59:59)
            const endOfDay = new Date(dateObj);
            endOfDay.setHours(23, 59, 59, 999);
            
            const startTS = Timestamp.fromDate(startOfDay);
            const endTS = Timestamp.fromDate(endOfDay);
            
            q = query(q, where('createdAt', '>=', startTS), where('createdAt', '<=', endTS));
        }


        // Add ordering
        q = query(q, orderBy('createdAt', 'desc'));

        // Set up real-time listener
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const orders = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as any));

                setData({ orders });
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error('❌ Error fetching all orders:', err);
                console.error('  Error code:', err.code);
                console.error('  Error message:', err.message);
                setError(err as Error);
                setLoading(false);
            }
        );

        // Cleanup listener on unmount
        return () => {
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restaurantId, startTimestamp]);
    return { data, loading, error };
};

/**
 * Custom hook to fetch a single order by ID in real-time from Firebase Firestore
 * @param orderId - The order ID to fetch
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseOrderById = (
    orderId: string
): UseFirebaseOrdersResult<any> => {
    const [data, setData] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!orderId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Reference to specific order document
        const orderDocRef = doc(db, 'Orders', orderId);

        // Set up real-time listener for single document
        const unsubscribe = onSnapshot(
            orderDocRef,
            (docSnap) => {
                if (docSnap.exists()) {
                    setData({
                        id: docSnap.id,
                        ...docSnap.data()
                    });
                    setLoading(false);
                    setError(null);
                } else {
                    setData(null);
                    setLoading(false);
                    setError(new Error('Order not found'));
                }
            },
            (err) => {
                console.error('❌ Error fetching order by ID:', err);
                console.error('  Error code:', err.code);
                console.error('  Error message:', err.message);
                setError(err as Error);
                setLoading(false);
            }
        );

        // Cleanup listener on unmount
        return () => {
            unsubscribe();
        };
    }, [orderId]);

    return { data, loading, error };
};

// Add this to your existing hooks file (useFirebaseOrders.ts)
/**
 * Custom hook to fetch child orders by parentOrderId in real-time from Firebase Firestore
 * @param parentOrderId - The parent order ID to fetch child orders for
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseChildOrders = (
    parentOrderId: string
): UseFirebaseOrdersResult<{ orders: any[] }> => {
    const [data, setData] = useState<{ orders: any[] } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!parentOrderId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const ordersRef = collection(db, 'Orders');
        let q = query(
            ordersRef,
            where('parentOrderId', '==', parentOrderId)
        );

        // Set up real-time listener
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const orders = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as any));
                
                // Sort manually on the client side
                orders.sort((a, b) => {
                    const aTime = a.createdAt?.seconds || 0;
                    const bTime = b.createdAt?.seconds || 0;
                    return aTime - bTime;
                });
                
                setData({ orders });
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error('❌ Error fetching child orders:', err);
                console.error('  Error code:', err.code);
                console.error('  Error message:', err.message);
                setError(err as Error);
                setLoading(false);
            }
        );

        // Cleanup listener on unmount
        return () => {
            unsubscribe();
        };
    }, [parentOrderId]);

    return { data, loading, error };
};

interface UseFirebaseOrdersResult<T> {
    data: T | null;
    loading: boolean;
    error: Error | null;
}

/**
 * Custom hook to fetch in-app cart orders in real-time from Firebase Firestore
 * @param restaurantId - The restaurant ID to filter orders
 * @param startDate - Start date for filtering (deliverAt >= startDate)
 * @param endDate - End date for filtering (deliverAt <= endDate)
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseInAppCartOrders = (
    restaurantId: string,
    startDate?: Date,
    endDate?: Date
): UseFirebaseOrdersResult<{ orders: any[] }> => {
    const [data, setData] = useState<{ orders: any[] } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Convert dates to timestamps for dependency comparison
    const startTimestamp = startDate ? startDate.getTime() : null;
    const endTimestamp = endDate ? endDate.getTime() : null;

    useEffect(() => {
        if (!restaurantId) {
            setLoading(false);
            return;
        }
        setLoading(true);

        // Build the query
        const ordersRef = collection(db, 'Orders');
        let q = query(
            ordersRef,
            where('restaurantId', '==', restaurantId),
            where('orderStatus', '==', 'InAppCart')
        );
        // Add date range filters if provided
        if (startDate) {
            const startTS = Timestamp.fromDate(startDate);
            q = query(q, where('deliverAt', '>=', startTS));
        }

        if (endDate) {
            const endTS = Timestamp.fromDate(endDate);
            q = query(q, where('deliverAt', '<=', endTS));
        }

        // Add ordering
        q = query(q, orderBy('createdAt', 'desc'));

        // Set up real-time listener
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const orders = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as any));
                setData({ orders });
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error('❌ Error fetching in-app cart orders:', err);
                console.error('  Error code:', err.code);
                console.error('  Error message:', err.message);
                setError(err as Error);
                setLoading(false);
            }
        );

        // Cleanup listener on unmount
        return () => {
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restaurantId, startTimestamp, endTimestamp]);
    return { data, loading, error };
};